/**
 * Claims types for identity verification (phone/email verification state)
 */

export const CLAIMS_LEVEL = {
  PHONE: 'phone',
  EMAIL: 'email',
  PHONE_OR_EMAIL: 'phone_or_email',
  PHONE_AND_EMAIL: 'phone_and_email',
  UNVERIFIED: 'unverified',
} as const;

export type ClaimsLevel = (typeof CLAIMS_LEVEL)[keyof typeof CLAIMS_LEVEL];

/**
 * The public view of a verification returned by the IDV endpoints. Mirrors the
 * backend `idv.Verification` (internal/idv/verification.go) — the client-relevant
 * subset of the verification, with secrets (code, token) omitted.
 */
export interface Verification {
  readonly id: string;
  readonly level: ClaimsLevel;
  readonly continue: string;
}

export interface Claims {
  readonly phoneVerifExp?: number; // Unix seconds
  readonly emailVerifExp?: number; // Unix seconds
  /**
   * Unix seconds until which the caller holds a migration proof — a recent
   * demonstration of account control (correct security answers via
   * POST /api/idv/migration-proof, given while email-verified) that authorizes
   * a phone migration. Ephemeral: rides only the JWT, never persisted server-side,
   * and gates nothing but the migration (see ClaimsLogic.mayMigratePhone).
   */
  readonly migrationProofExp?: number; // Unix seconds
}

/** A verification expiration as both a raw Date and a human-friendly string. */
export interface VerificationExpiration {
  readonly date: Date;
  /** e.g. "30 days", "5 hours", "less than an hour" */
  readonly formattedDate: string;
}
